import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, AlertTriangle, Pen, Shield, 
  Calendar, User, Mail, Clock, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignDocument = () => {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signatureCanvas, setSignatureCanvas] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchDocumentData();
  }, [requestId, token, email]);

  const fetchDocumentData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/esignature-workflow/${requestId}/document?token=${token}&email=${encodeURIComponent(email || '')}`);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to load document');
      }
      
      const data = await res.json();
      setDocumentData(data);
      
      // Check if current user already signed
      const currentRecipient = data.recipients?.find(r => r.email?.toLowerCase() === email?.toLowerCase());
      if (currentRecipient?.signed) {
        setSigned(true);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Canvas signature handling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    
    setSignatureCanvas(ctx);
  }, [loading]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    signatureCanvas.beginPath();
    signatureCanvas.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !signatureCanvas) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    signatureCanvas.lineTo(x, y);
    signatureCanvas.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!signerName.trim()) {
      toast.error('Please enter your full legal name');
      return;
    }
    
    if (!hasSignature) {
      toast.error('Please provide your signature');
      return;
    }
    
    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    setSigning(true);
    try {
      // Get signature as base64 image
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      
      const res = await fetch(`${API}/esignature-workflow/${requestId}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          request_id: requestId,
          signer_email: email,
          signer_name: signerName,
          signature_data: signatureData
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Document signed successfully!');
        setSigned(true);
      } else {
        toast.error(data.error || 'Failed to sign document');
      }
    } catch (err) {
      toast.error('Error signing document: ' + err.message);
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b8860b]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#0d1d35] border-red-500/50">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Unable to Load Document</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#0d1d35] border-green-500/50">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Document Signed Successfully</h2>
            <p className="text-gray-400 mb-4">
              Thank you! Your signature has been captured and the document has been updated.
            </p>
            <div className="p-4 bg-[#0a1628] rounded-lg text-left mb-4">
              <p className="text-sm text-gray-400"><strong>Document:</strong> {documentData?.document_name}</p>
              <p className="text-sm text-gray-400"><strong>Signed by:</strong> {email}</p>
              <p className="text-sm text-gray-400"><strong>Date:</strong> {new Date().toLocaleString()}</p>
            </div>
            <p className="text-xs text-gray-500">You will receive a confirmation email with the signed document.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] py-8 px-4" data-testid="sign-document-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-8 h-8 text-[#b8860b]" />
            <span className="text-2xl font-bold text-white">BETHEREUM</span>
          </div>
          <h1 className="text-xl text-white">Document Signature Request</h1>
          <p className="text-sm text-gray-400 mt-2">Secure Electronic Signature</p>
        </div>

        {/* Document Info Card */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a] mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#b8860b]" />
              {documentData?.document_name}
            </CardTitle>
            <CardDescription>
              {documentData?.agreement_type?.replace(/_/g, ' ').toUpperCase()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <User className="w-4 h-4" />
                <span>From: {documentData?.sender_name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>{new Date(documentData?.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            
            {/* Recipients Status */}
            <div className="mt-4 pt-4 border-t border-[#1a2d4a]">
              <h4 className="text-xs text-gray-500 uppercase mb-2">Signature Status</h4>
              <div className="space-y-2">
                {documentData?.recipients?.map((recipient, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-[#0a1628] rounded">
                    <span className="text-sm text-gray-300">{recipient.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${recipient.signed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {recipient.signed ? 'Signed' : 'Pending'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signature Form */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Pen className="w-5 h-5 text-[#b8860b]" />
              Sign Document
            </CardTitle>
            <CardDescription>
              Please review the document and provide your signature below
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Full Legal Name */}
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wider">Full Legal Name</label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Enter your full legal name"
                className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                data-testid="signer-name-input"
              />
            </div>
            
            {/* Signature Pad */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-400 uppercase tracking-wider">Your Signature</label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearSignature}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Clear
                </Button>
              </div>
              <div className="border-2 border-dashed border-[#1a2d4a] rounded-lg bg-white p-2">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Draw your signature above using your mouse or finger</p>
            </div>
            
            {/* Terms Agreement */}
            <div className="p-4 bg-[#0a1628] rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1"
                  data-testid="agree-checkbox"
                />
                <span className="text-sm text-gray-300">
                  I agree that my electronic signature is legally binding and has the same effect as a handwritten signature. 
                  I confirm that I have reviewed the document and consent to its terms.
                </span>
              </label>
            </div>
            
            {/* Sign Button */}
            <Button 
              onClick={handleSign}
              disabled={signing || !signerName || !hasSignature || !agreedToTerms}
              className="w-full bg-[#b8860b] hover:bg-[#9a7209] text-white"
              data-testid="sign-button"
            >
              {signing ? 'Signing...' : 'Sign Document'}
              <Pen className="w-4 h-4 ml-2" />
            </Button>
            
            <p className="text-xs text-center text-gray-500">
              By clicking "Sign Document", you acknowledge that your electronic signature constitutes 
              your legal acceptance of the terms contained in this document.
            </p>
          </CardContent>
        </Card>
        
        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
            <Shield className="w-3 h-3" />
            Secured with 256-bit encryption • Blockchain verified
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignDocument;
